import { Field, FieldArray, Formik, FormikHelpers as FormikActions } from 'formik';
import { default as React, useMemo } from 'react';
import { Button } from '~/components/Button';
import { Input, InputType } from '~/components/Controls/Input';
import { Pagination } from '~/components/Pagination';
import { useConfirm } from '~/components/Dialogs';
import { CopyableText } from '~/components/CopyableText';
import { Table } from '~/components/Table';
import {
    ButtonField, CheckboxField,
    EditorField,
    Form,
    FormColumn,
    FormControls,
    FormRow,
    FormTitle,
    hasErrors,
    InputField,
    IntentText,
    TagsField,
} from '~/components/Form';
import { IModalComponent, IModalComponentClose } from '~/components/Modal';
import { SelectAdTypeField } from '~/components/SelectAdType';
import { SelectCountryField } from '~/components/SelectCountry';
import { TabsWithBody } from '~/components/Tabs';
import { useAbility } from '~/lib/ability';
import { getApiErrorMessage } from '~/lib/errors/apiError';
import { renderBoolean } from '~/lib/helpers';
import { Intent } from '~/lib/intent';
import { prettyJSON } from '~/lib/json';
import { MultiSelectAgencyField } from '~/modules/Clients/components/SelectAgency';
import { SelectChargeSchemaField } from '~/modules/Clients/components/SelectChargeSchema';
import { SelectSellerField } from '~/modules/Clients/components/SelectSeller';
import { SelectSourceCategoryField } from '~/modules/Clients/components/SelectSourceCategory';
import { SelectTrafficSourceField } from '~/modules/Clients/components/SelectTrafficSource';
import { SourcesAbility } from '~/modules/Sources/ability';
import style from '~/modules/Sources/forms/index.module.scss';
import { defaultFeeOverrideSetting } from '~/modules/Sources/models/FeeOverrideSetting';
import { defaultSourceDomain } from '~/modules/Sources/models/IDomains';
import { defaultPlacement } from '~/modules/Sources/models/Placement';
import {
    ISource,
    RoundingAdType,
    roundingFieldNames,
    roundingLabel,
    roundingName,
    SourceSchema
} from '~/modules/Sources/models/Source';

import { useModals } from '~/components/Modal';
import { PlacementForm } from '~/modules/Sources/forms/PlacementForm';
import { ImportPlacementsButton } from '~/modules/Sources/components/ImportPlacementsButton';
import { createPlacement, updatePlacement, deleteSellerPlacement } from '~/modules/Sources/api';
import { usePlacements } from '~/modules/Sources/usePlacements';
import { PlacementsSearch } from '~/modules/Sources/components/PlacementsSearch';
import { Trash2 } from "lucide";
import { LucideIcon } from "~/components/LucideIcon";

interface ISourceForm {
    source?: ISource;
    lockSeller?: boolean;
    onSubmit?: (values: ISource, formikActions: FormikActions<ISource>, close: IModalComponentClose) => void;
}

export const SourceForm: IModalComponent<ISourceForm> = props => {
    const ability = useAbility(SourcesAbility);
    const confirm = useConfirm();

    const sourceId = props.source?.id;
    const placements = usePlacements(sourceId);

    const normalizePriceRoundingValue = (value: string | number[] | undefined) => {
        if (Array.isArray(value)) {
            return value.join('\n');
        }
        return value || '';
    };

    const normalizePriceRounding = (priceRounding: ISource['priceRounding']) => ({
        [RoundingAdType.BANNER]: normalizePriceRoundingValue(priceRounding?.[RoundingAdType.BANNER]),
        [RoundingAdType.VIDEO]: normalizePriceRoundingValue(priceRounding?.[RoundingAdType.VIDEO]),
        [RoundingAdType.NATIVE]: normalizePriceRoundingValue(priceRounding?.[RoundingAdType.NATIVE]),
        [RoundingAdType.INTERSTITIAL]: normalizePriceRoundingValue(priceRounding?.[RoundingAdType.INTERSTITIAL]),
    });

    const normalizeConfiguration = (configuration: ISource['configuration']) => {
        if (configuration == null) {
            return '';
        }
        if (typeof configuration === 'string') {
            try {
                return prettyJSON(configuration);
            } catch {
                return configuration;
            }
        }
        try {
            return prettyJSON(JSON.stringify(configuration));
        } catch {
            return '';
        }
    };

    const initialValues = useMemo(() => {
        const priceRounding = normalizePriceRounding(props.source?.priceRounding);
        const configuration = normalizeConfiguration(props.source?.configuration);

        return {
            ...props.source,
            priceRounding,
            configuration,
        } as ISource;
    }, [props.source]);

    const modals = useModals();

    const createPlacementModal = modals.create(PlacementForm, {
        onSubmit: (newPlacement, actions, close) => {
            createPlacement(sourceId, newPlacement)
                .then(() => {
                    close();
                    placements.reload();
                })
                .catch(error => {
                    actions.setStatus(getApiErrorMessage(error, 'Error creating placement'));
                    actions.setSubmitting(false);
                });
        }
    });

    const editPlacementModal = modals.create(PlacementForm, {
        onSubmit: (updatedPlacement, actions, close) => {
            updatePlacement(sourceId, updatedPlacement)
                .then(() => {
                    close();
                    placements.reload();
                })
                .catch(error => {
                    actions.setStatus(getApiErrorMessage(error, 'Error updating placement'));
                    actions.setSubmitting(false);
                });
        },
    });

    return <Formik initialValues={initialValues}
        validationSchema={SourceSchema}
        onSubmit={(source, actions) => {
            const cleaned = {
                ...source,
                domains: source.domains.map(domain => ({
                    ...domain,
                    customId:
                        domain.customId == null || domain.customId.trim() === ''
                            ? null
                            : domain.customId,
                })),
            };

            props.onSubmit(cleaned, actions, props.close);
        }}
    >
        {formik => {
            const { values: source, status, isSubmitting } = formik;
            return <Form>
                <FormTitle>{source.id ? (ability.canOnlyView() ? 'View Source' : 'Edit Source') : 'Create Source'}</FormTitle>
                {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
                {ability.canOnlyView() ?
                    <TabsWithBody tabs={[{
                        title: 'Rounding',
                        intent: hasErrors(formik, [roundingFieldNames()]) && Intent.DANGER,
                        children: <div>
                            <FormRow noBorder>
                                <FormColumn>
                                    <Field name={roundingName(RoundingAdType.BANNER)}
                                        label={roundingLabel(RoundingAdType.BANNER)}
                                        options={{
                                            mode: 'text',
                                            canCopy: true,
                                            disabled: isSubmitting || ability.canOnlyView(),
                                            minLines: 6,
                                            maxLines: 6
                                        }}
                                        component={EditorField}
                                    />
                                    <Field name={roundingName(RoundingAdType.VIDEO)}
                                        label={roundingLabel(RoundingAdType.VIDEO)}
                                        options={{
                                            mode: 'text',
                                            canCopy: true,
                                            disabled: isSubmitting || ability.canOnlyView(),
                                            minLines: 6,
                                            maxLines: 6
                                        }}
                                        component={EditorField}
                                    />
                                    <Field name={roundingName(RoundingAdType.NATIVE)}
                                        label={roundingLabel(RoundingAdType.NATIVE)}
                                        options={{
                                            mode: 'text',
                                            canCopy: true,
                                            disabled: isSubmitting || ability.canOnlyView(),
                                            minLines: 6,
                                            maxLines: 6
                                        }}
                                        component={EditorField}
                                    />
                                    <Field name={roundingName(RoundingAdType.INTERSTITIAL)}
                                        label={roundingLabel(RoundingAdType.INTERSTITIAL)}
                                        options={{
                                            mode: 'text',
                                            canCopy: true,
                                            disabled: isSubmitting || ability.canOnlyView(),
                                            minLines: 6,
                                            maxLines: 6
                                        }}
                                        component={EditorField}
                                    />
                                </FormColumn>
                            </FormRow>
                        </div>
                    }]} /> :
                    <TabsWithBody tabs={[{
                        title: 'General',
                        intent:
                            hasErrors(
                                formik,
                                ['sellerId', 'name', 'ks', 'fee', 'chargeSchema', 'tmax', 'scale', 'trafficSource', 'maxRps', 'domain']
                            ) && Intent.DANGER,
                        children: <FormRow noBorder>
                            <FormColumn>
                                <Field name='sellerId'
                                    label='Seller'
                                    options={{
                                        placeholder: 'Seller',
                                        clearable: true,
                                        searchable: true,
                                        disabled: props.lockSeller || isSubmitting
                                    }}
                                    component={SelectSellerField}
                                />
                                <Field name='name'
                                    label='Name'
                                    options={{
                                        disabled: isSubmitting
                                    }}
                                    component={InputField}
                                />
                                <Field name='ks'
                                    label='Display Manager'
                                    options={{
                                        disabled: isSubmitting
                                    }}
                                    component={InputField}
                                />
                                <Field name='tags'
                                    label='Tags'
                                    options={{
                                        placeholder: 'Tags',
                                        disabled: isSubmitting,
                                        dropdown: true
                                    }}
                                    component={TagsField}
                                />
                            </FormColumn>
                            <FormColumn>
                                <Field name='fee'
                                    label='Exchange Fee'
                                    type='number'
                                    options={{
                                        disabled: isSubmitting,
                                        type: InputType.INTEGER,
                                        min: 0,
                                        step: 1
                                    }}
                                    component={InputField}
                                />
                                <Field name='tmax'
                                    label='TMax'
                                    type='number'
                                    options={{
                                        disabled: isSubmitting,
                                        type: InputType.INTEGER,
                                        min: 0,
                                        step: 1
                                    }}
                                    component={InputField}
                                />
                                <Field name='scale'
                                    label='Scale'
                                    options={{
                                        onOmitChange: omit => omit ? null : 0.1,
                                        placeholder: 'Scale',
                                        type: InputType.FLOAT,
                                        disabled: isSubmitting,
                                        min: 0,
                                        step: 0.01
                                    }}
                                    component={InputField}
                                />
                                <Field name='maxRps'
                                    label='Max RPS'
                                    options={{
                                        onOmitChange: omit => omit ? null : 100000,
                                        placeholder: 'Max RPS',
                                        type: InputType.INTEGER,
                                        disabled: isSubmitting,
                                        min: 0,
                                        step: 1
                                    }}
                                    component={InputField}
                                />
                                <Field name='chargeSchema'
                                    label='Charge schema'
                                    options={{
                                        placeholder: 'Charge schema',
                                        disabled: isSubmitting,
                                    }}
                                    component={SelectChargeSchemaField}
                                />
                                <Field name='trafficSource'
                                    label='Traffic Source'
                                    options={{
                                        placeholder: 'Traffic source',
                                        disabled: isSubmitting,
                                    }}
                                    component={SelectTrafficSourceField}
                                />
                                <Field name='category'
                                    label='Category'
                                    options={{
                                        placeholder: 'Category',
                                        disabled: isSubmitting,
                                    }}
                                    component={SelectSourceCategoryField}
                                />
                            </FormColumn>
                        </FormRow>
                    }, {
                        title: 'Blockers',
                        intent: hasErrors(formik, ['bcat', 'bapp', 'badv']) && Intent.DANGER,
                        children: <FormRow noBorder>
                            <FormColumn>
                                <Field name='bcat'
                                    label='Categories'
                                    options={{
                                        placeholder: 'Categories',
                                        disabled: isSubmitting
                                    }}
                                    component={TagsField}
                                />
                                <Field name='bapp'
                                    label='Applications'
                                    options={{
                                        placeholder: 'Applications',
                                        disabled: isSubmitting
                                    }}
                                    component={TagsField}
                                />
                                <Field name='badv'
                                    label='Advertising Domains'
                                    options={{
                                        placeholder: 'Advertising Domains',
                                        disabled: isSubmitting
                                    }}
                                    component={TagsField}
                                />
                            </FormColumn>
                        </FormRow>
                    }, {
                        title: 'Configuration',
                        intent: hasErrors(formik, ['configuration']) && Intent.DANGER,
                        children: <FormRow noBorder>
                            <FormColumn>
                                <Field name='configuration'
                                    options={{
                                        mode: 'json',
                                        disabled: isSubmitting,
                                        canFullScreen: true
                                    }}
                                    component={EditorField}
                                />
                            </FormColumn>
                        </FormRow>
                    }, {
                        title: 'Rounding',
                        intent: hasErrors(formik, [roundingFieldNames()]) && Intent.DANGER,
                        children: <div>
                            <FormRow noBorder>
                                <FormColumn>
                                    <Field name={roundingName(RoundingAdType.BANNER)}
                                        label={roundingLabel(RoundingAdType.BANNER)}
                                        options={{
                                            mode: 'text',
                                            canCopy: true,
                                            disabled: isSubmitting,
                                            minLines: 6,
                                            maxLines: 6
                                        }}
                                        component={EditorField}
                                    />
                                    <Field name={roundingName(RoundingAdType.VIDEO)}
                                        label={roundingLabel(RoundingAdType.VIDEO)}
                                        options={{
                                            mode: 'text',
                                            canCopy: true,
                                            disabled: isSubmitting,
                                            minLines: 6,
                                            maxLines: 6
                                        }}
                                        component={EditorField}
                                    />
                                    <Field name={roundingName(RoundingAdType.NATIVE)}
                                        label={roundingLabel(RoundingAdType.NATIVE)}
                                        options={{
                                            mode: 'text',
                                            canCopy: true,
                                            disabled: isSubmitting,
                                            minLines: 6,
                                            maxLines: 6
                                        }}
                                        component={EditorField}
                                    />
                                    <Field name={roundingName(RoundingAdType.INTERSTITIAL)}
                                        label={roundingLabel(RoundingAdType.INTERSTITIAL)}
                                        options={{
                                            mode: 'text',
                                            canCopy: true,
                                            disabled: isSubmitting,
                                            minLines: 6,
                                            maxLines: 6
                                        }}
                                        component={EditorField}
                                    />
                                </FormColumn>
                            </FormRow>
                        </div>
                    }, {
                        title: 'Fee override',
                        intent: hasErrors(formik, ['feeOverrideConfiguration']) && Intent.DANGER,
                        children:
                            <FieldArray
                                name="feeOverrideConfiguration"
                                render={arrayHelpers => (
                                    <div className={style.sourceForm}>
                                        {source.feeOverrideConfiguration.map((setting, index) => (
                                            <FormRow noBorder noMargin key={index} className={style.feeOverrideItem}>
                                                <div className={style.feeOverrideRow}>
                                                    <Field
                                                        name={`feeOverrideConfiguration.${index}.adType`}
                                                        label="Ad type"
                                                        className={style.feeOverrideField}
                                                        options={{
                                                            placeholder: 'Ad type',
                                                            clearable: true,
                                                            disabled: isSubmitting
                                                        }}
                                                        component={SelectAdTypeField}
                                                    />

                                                    <Field
                                                        name={`feeOverrideConfiguration.${index}.country`}
                                                        label="Country"
                                                        className={style.feeOverrideField}
                                                        options={{
                                                            placeholder: 'Country',
                                                            searchable: true,
                                                            disabled: isSubmitting,
                                                            clearable: true,
                                                            short: true
                                                        }}
                                                        component={SelectCountryField}
                                                    />

                                                    <Field
                                                        name={`feeOverrideConfiguration.${index}.scale`}
                                                        label='Scale'
                                                        type='number'
                                                        className={style.feeOverrideNumber}
                                                        options={{
                                                            placeholder: 'Scale',
                                                            type: InputType.FLOAT,
                                                            disabled: isSubmitting,
                                                            min: 0,
                                                            step: 0.01
                                                        }}
                                                        component={InputField}
                                                    />

                                                    <Field
                                                        name={`feeOverrideConfiguration.${index}.fee`}
                                                        label='Fee'
                                                        type='number'
                                                        className={style.feeOverrideNumber}
                                                        options={{
                                                            placeholder: 'Fee',
                                                            disabled: isSubmitting,
                                                            type: InputType.INTEGER,
                                                            min: 0,
                                                            step: 1
                                                        }}
                                                        component={InputField}
                                                    />

                                                    {ability.canDelete() && (
                                                        <Field
                                                            name={'delete'}
                                                            className={style.feeOverrideBtn}
                                                            options={{
                                                                onClick: () => confirm('Delete setting?', {
                                                                    onOk: () => arrayHelpers.remove(index),
                                                                    icon: <LucideIcon icon={Trash2} size={20} />,
                                                                    intent: Intent.DANGER
                                                                }),
                                                                icon: <i className='fa fa-trash' />,
                                                                intent: Intent.DANGER
                                                            }}
                                                            component={ButtonField}
                                                        />
                                                    )}
                                                </div>
                                            </FormRow>
                                        ))}
                                        {ability.canEdit() && (
                                            <div className={style.addDomainBtn}>
                                                <Button
                                                    type="button"
                                                    disabled={isSubmitting}
                                                    intent={Intent.PRIMARY}
                                                    onClick={() => {
                                                        arrayHelpers.push(defaultFeeOverrideSetting);
                                                    }}
                                                >
                                                    Add fee override
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            />
                    }, {
                        title: 'Qualified domains',
                        intent: hasErrors(formik, ['domains']) && Intent.DANGER,
                        children:
                            <FieldArray
                                name="domains"
                                render={arrayHelpers => (
                                    <div>
                                        {source.domains.map((setting, index) => {
                                            const domainErrors = (formik.errors.domains as any)?.[index];
                                            const domainFields: [string, string][] = [
                                                ['domain', 'Domain'],
                                                ['customId', 'Custom ID'],
                                                ['bundles', 'Bundles'],
                                            ];
                                            const rowErrors: string[] =
                                                formik.submitCount > 0 && domainErrors
                                                    ? domainFields
                                                        .filter(([key]) => typeof domainErrors[key] === 'string')
                                                        .map(([key, label]) => `${label}: ${domainErrors[key]}`)
                                                    : [];
                                            return (
                                            <FormRow noBorder noMargin key={index} className={style.domainsList}>
                                                <div className={style.domainsRow}>
                                                <div className={style.domainsForm}>
                                                    <Field name={`domains.${index}.domain`}
                                                        label='Domain'
                                                        className={style.domainsInput}
                                                        hideError
                                                        options={{
                                                            placeholder: 'Domain',
                                                            disabled: isSubmitting
                                                        }}
                                                        component={InputField}
                                                    />
                                                    <Field
                                                        name={`domains.${index}.customId`}
                                                        label='Custom ID'
                                                         className={`${style.domainsInput} ${style.domainsInputId}`}
                                                        hideError
                                                        options={{
                                                            placeholder: 'Custom ID',
                                                            disabled: isSubmitting
                                                        }}
                                                        component={InputField}
                                                    />
                                                    <Field name={`domains.${index}.bundles`}
                                                        label='Bundles'
                                                        className={style.domainsInput}
                                                        hideError
                                                        options={{
                                                            placeholder: 'Bundles',
                                                            disabled: isSubmitting
                                                        }}
                                                        component={InputField}
                                                    />
                                                    <Field name={`domains.${index}.isDefault`}
                                                        label='Default'
                                                        className={style.domainsCheckbox}
                                                        options={{
                                                            placeholder: 'Is default',
                                                            disabled: isSubmitting
                                                        }}
                                                        component={CheckboxField}
                                                    />

                                                    {ability.canDelete() &&
                                                        <Field name={'delete'}
                                                            className={style.domainsBtn}
                                                            options={{
                                                                onClick: () => confirm('Delete domain?', {
                                                                    onOk: () => arrayHelpers.remove(index),
                                                                    icon: <i
                                                                        className='fa fa-trash' />,
                                                                    intent: Intent.DANGER
                                                                }),
                                                                icon: <i
                                                                    className='fa fa-trash' />,
                                                                intent: Intent.DANGER
                                                            }}
                                                            component={ButtonField} />}
                                                </div>
                                                {rowErrors.map((error, errorIndex) => (
                                                    <IntentText
                                                        key={errorIndex}
                                                        intent={Intent.DANGER}
                                                        className={style.domainsError}
                                                    >
                                                        {error}
                                                    </IntentText>
                                                ))}
                                                </div>
                                            </FormRow>
                                            );
                                        })}
                                        {ability.canEdit() &&
                                        <div className={style.addDomainBtn}>
                                            <Button
                                                type="button"
                                                intent={Intent.PRIMARY}
                                                onClick={() => {
                                                    arrayHelpers.push(defaultSourceDomain);
                                                }}
                                            >
                                                Add domain
                                            </Button>
                                        </div>}
                                    </div>
                                )}
                            />
                    },
                    {
                        title: 'Placements',
                        children: source.id ? (
                            <div className={style.placementsForm}>
                                <PlacementsSearch placements={placements} className={style.placementsSearch} />
                                <Table
                                    header
                                    records={placements.items}
                                    columns={[
                                        { header: 'Name', field: 'name', cellWrap: 'break-word', headerAttrs: { className: style.nameCell }, cellAttrs: { className: style.nameCell } },
                                        { header: 'ID', cellWrap: 'break-word', headerAttrs: { className: style.idCell }, cellAttrs: { className: style.idCell }, cell: (placement) => <CopyableText>{placement.id || ''}</CopyableText> },
                                        { header: 'Platform', field: 'platform' },
                                        { header: 'Bundle', field: 'bundle', cellWrap: 'break-word', headerAttrs: { className: style.bundleCell }, cellAttrs: { className: style.bundleCell } },
                                        { header: 'Ad Type', field: 'adType' },
                                        { header: 'Placement Type', field: 'placementType' },
                                        { header: 'HVA', cell: (placement) => renderBoolean(placement.hva) },
                                        { header: 'Pricefloor', field: 'pricefloor' },
                                        { header: 'MAX Ad Unit ID', field: 'maxAdUnitId', cellWrap: 'break-word' },
                                        { header: 'GPID', field: 'gpid', cellWrap: 'break-word' },
                                        {
                                            header: '',
                                            small: true,
                                            hide: !ability.canDelete(),
                                            cell: (placement) => (
                                                <div className={style.placementActions}>
                                                    <Button small responsive
                                                        leftIcon={<i className='fa fa-edit' />}
                                                        onClick={() => editPlacementModal.show({ placement })}
                                                    />
                                                    <Button small responsive
                                                        leftIcon={<i className='fa fa-trash' />}
                                                        intent={Intent.DANGER}
                                                        onClick={() =>
                                                            confirm('Delete placement?', {
                                                                onOk: () =>
                                                                    deleteSellerPlacement(placement.id, sourceId).then(() =>
                                                                        placements.reload(),
                                                                    ),
                                                            })
                                                        }
                                                    />
                                                </div>
                                            ),
                                        },
                                    ]}
                                />

                                <Pagination
                                    padding={4}
                                    limit={placements.pageSize}
                                    offset={placements.pageNo * placements.pageSize}
                                    total={placements.count}
                                    onChange={placements.changePage}
                                    limits={[25, 50, 100, 200]}
                                />

                                {ability.canEdit() && (
                                    <FormControls>
                                        <ImportPlacementsButton sellerId={source.sellerId} onImported={placements.reload} />
                                        <Button
                                            type="button"
                                            intent={Intent.PRIMARY}
                                            onClick={() => createPlacementModal.show({
                                                placement: defaultPlacement
                                            })}
                                        >
                                            Add Placement
                                        </Button>
                                    </FormControls>
                                )}
                            </div>
                       ) : (
                        <div>Please save the Source before adding placements</div>
                         )
                        }
                    ]} />
                }
                <FormControls>
                    <Button disabled={isSubmitting}
                        onClick={props.close}
                    >{ability.canOnlyView() ? 'Close' : 'Cancel'}</Button>
                    {!ability.canOnlyView() && (
                        <Button
                            type='submit'
                            disabled={isSubmitting}
                            intent={Intent.PRIMARY}
                            rightIcon={isSubmitting && <i className='fa fa-spinner fa-spin' />}
                        >
                            Save
                        </Button>
                    )}
                </FormControls>
            </Form>;
        }}
    </Formik>;
};
